import { JSX } from "react"
import {
    AnchorHTMLAttributes,
    ComponentType,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react"
import { RouteObject, useLocation, useNavigate, useParams } from "react-router"
import { Link as RouterLink } from "react-router-dom"

export type RouteTree = { readonly [key: string]: RouteNode }

export type RouteNode = {
    readonly param?: true
    readonly component?: ComponentType
    readonly index?: ComponentType
    readonly errorComponent?: ComponentType
    readonly children?: RouteTree
}

// Extracts `:param` names from a flat path string like "workspace/:workspaceId/project/:projectId".
type ParamsFromPath<S extends string> = S extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ParamsFromPath<Rest>
    : S extends `${string}:${infer Param}`
      ? Param
      : never

// `{...}[keyof T]` produces a *union* of per-key Records. `keyof (A | B)` is `keyof A & keyof B`,
// so all path keys would collapse to `never`. UnionToIntersection converts that union to a single
// intersection so every path key is visible via `keyof PathMap<T>`.
type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (
    x: infer I
) => void
    ? I
    : never

type PathMap<
    T extends RouteTree,
    Prefix extends string = "",
    Accumulated extends string = never,
> = UnionToIntersection<
    {
        [K in keyof T & string]: T[K] extends { param: true }
            ? Record<`${Prefix}/:${K}`, Accumulated | K> &
                  (T[K] extends { children: infer C extends RouteTree }
                      ? PathMap<C, `${Prefix}/:${K}`, Accumulated | K>
                      : Record<never, never>)
            : K extends "/"
              ? Record<"/", Accumulated> &
                    (T[K] extends { children: infer C extends RouteTree }
                        ? PathMap<C, "", Accumulated>
                        : Record<never, never>)
              : K extends `${string}/${string}`
                ? Record<`/${K}`, ParamsFromPath<K>>
                : K extends "*"
                  ? Record<`${Prefix}/*`, Accumulated>
                  : Record<`${Prefix}/${K}`, Accumulated> &
                        (T[K] extends { children: infer C extends RouteTree }
                            ? PathMap<C, `${Prefix}/${K}`, Accumulated>
                            : Record<never, never>)
    }[keyof T & string]
>

type BuildScopePaths<T extends RouteTree, Prefix extends string = ""> = {
    [K in keyof T & string]:
        | (T[K] extends { param: true } ? `${Prefix}/:${K}` : K extends "/" ? "/" : never)
        | (T[K] extends { children: infer C extends RouteTree }
              ? BuildScopePaths<
                    C,
                    T[K] extends { param: true }
                        ? `${Prefix}/:${K}`
                        : K extends "/"
                          ? ""
                          : `${Prefix}/${K}`
                >
              : never)
}[keyof T & string]

type ScopePrefix<TScope extends string> = TScope extends "/" ? "" : TScope

type DescendantPaths<TScope extends string, TMap> = {
    [K in keyof TMap & string]: K extends `${ScopePrefix<TScope>}${infer Rest}`
        ? Rest extends ""
            ? "/"
            : Rest
        : never
}[keyof TMap & string]

type FullPath<TScope extends string, TRel extends string> = TScope extends "/"
    ? TRel
    : `${TScope}${TRel}`

type CallSiteParams<TScope extends string, TRel extends string, TMap> = Exclude<
    FullPath<TScope, TRel> extends keyof TMap ? TMap[FullPath<TScope, TRel>] & string : never,
    TScope extends keyof TMap ? TMap[TScope] & string : never
>

type LinkSearch = { search?: Record<string, string> }

type LinkTo<TScope extends string, TRel extends string, TMap> = LinkSearch &
    (TRel extends `${string}*`
        ? [CallSiteParams<TScope, TRel, TMap>] extends [never]
            ? { path: TRel; splat: string }
            : {
                  path: TRel
                  splat: string
                  params: Record<CallSiteParams<TScope, TRel, TMap>, string>
              }
        : [CallSiteParams<TScope, TRel, TMap>] extends [never]
          ? { path: TRel }
          : { path: TRel; params: Record<CallSiteParams<TScope, TRel, TMap>, string> })

export type GetScopedTo<T extends RouteTree, TScope extends BuildScopePaths<T>> = {
    [TRel in DescendantPaths<TScope, PathMap<T>>]: LinkTo<TScope, TRel, PathMap<T>>
}[DescendantPaths<TScope, PathMap<T>>]

type NavigationState = string | Record<string, unknown>

type ScopedRouter<TScope extends string, TMap> = {
    navigate: <TRel extends DescendantPaths<TScope, TMap>>(
        to: LinkTo<TScope, TRel, TMap>,
        options?: { replace?: boolean; state?: NavigationState }
    ) => void
    Link: <TRel extends DescendantPaths<TScope, TMap>>(
        props: PropsWithChildren<
            {
                to: LinkTo<TScope, TRel, TMap>
                state?: NavigationState
            } & AnchorHTMLAttributes<HTMLAnchorElement>
        >
    ) => JSX.Element
    preview: <TRel extends DescendantPaths<TScope, TMap>>(to: LinkTo<TScope, TRel, TMap>) => string
}

export type RouterInstance<T extends RouteTree> = {
    routes: RouteObject[]
    useRouter: <TScope extends BuildScopePaths<T>>(
        scope: TScope
    ) => ScopedRouter<TScope, PathMap<T>>
}

const keyToRoutePath = (key: string, isParam: boolean): string => {
    if (key === "/") return "/"
    if (key === "*") return "*"
    if (isParam) return `:${key}`
    return key
}

const buildRouteNodes = (tree: RouteTree, prefix = ""): RouteObject[] =>
    Object.entries(tree).flatMap(([key, node]) => {
        const segment = keyToRoutePath(key, node.param === true)
        const fullSegment = key === "/" ? "/" : prefix ? `${prefix}/${segment}` : segment

        if (!node.component) return node.children ? buildRouteNodes(node.children, fullSegment) : []

        const indexChild: RouteObject[] = node.index ? [{ index: true, Component: node.index }] : []
        const children = node.children ? buildRouteNodes(node.children) : []
        const allChildren = [...indexChild, ...children]

        return [
            {
                path: fullSegment,
                Component: node.component,
                ErrorBoundary: node.errorComponent,
                ...(allChildren.length > 0 ? { children: allChildren } : {}),
            },
        ]
    })

const resolvePath = (
    scope: string,
    path: string,
    urlParams: Record<string, string | undefined>,
    pathParams: Record<string, string>,
    splat?: string
) => {
    const normalizedScope = scope === "/" ? "" : scope
    const normalizedPath = normalizedScope && path === "/" ? "" : path
    const resolved = `${normalizedScope}${normalizedPath}`.replace(/:(\w+)/g, (_, key: string) => {
        const value = pathParams[key] ?? urlParams[key]
        if (typeof value === "undefined") throw new Error(`Missing route param: "${key}"`)
        return value
    })
    if (splat === undefined) return resolved
    return resolved.replace(/\*$/, splat)
}

type BaseLinkTo = {
    path: string
    params?: Record<string, string>
    splat?: string
    search?: Record<string, string>
}

export const useNavigationState = (onState: (state: unknown) => void): void => {
    const location = useLocation()
    const navigate = useNavigate()
    const handler = useRef(onState)
    handler.current = onState

    useEffect(() => {
        if (location.state === null || location.state === undefined) return
        const state: unknown = location.state
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: null }
        )
        handler.current(state)
    }, [location, navigate])
}

export const createRouter = <T extends RouteTree>(tree: T): RouterInstance<T> => {
    type TMap = PathMap<T>

    const routes = buildRouteNodes(tree)

    const useRouter = <TScope extends BuildScopePaths<T>>(
        scope: TScope
    ): ScopedRouter<TScope, TMap> => {
        const urlParams = useParams()
        const navigateHook = useNavigate()

        const resolveFromTo = useCallback(
            <TRel extends DescendantPaths<TScope, TMap>>(to: LinkTo<TScope, TRel, TMap>) => {
                const target = to as BaseLinkTo
                const pathParams = target.params ?? {}
                const path = resolvePath(scope, target.path, urlParams, pathParams, target.splat)
                const search = new URLSearchParams(target.search ?? {}).toString()
                return search ? `${path}?${search}` : path
            },
            [scope, urlParams]
        )

        const navigate = useCallback(
            <TRel extends DescendantPaths<TScope, TMap>>(
                to: LinkTo<TScope, TRel, TMap>,
                options?: { replace?: boolean; state?: NavigationState }
            ) => {
                navigateHook(resolveFromTo(to), options)
            },
            [navigateHook, resolveFromTo]
        )

        const preview = useCallback(
            <TRel extends DescendantPaths<TScope, TMap>>(to: LinkTo<TScope, TRel, TMap>) => {
                return resolveFromTo(to)
            },
            [resolveFromTo]
        )

        const Link = useCallback(
            <TRel extends DescendantPaths<TScope, TMap>>({
                to,
                children,
                state,
                ...rest
            }: PropsWithChildren<
                {
                    to: LinkTo<TScope, TRel, TMap>
                    state?: NavigationState
                } & AnchorHTMLAttributes<HTMLAnchorElement>
            >): JSX.Element => {
                return (
                    <RouterLink to={resolveFromTo(to)} state={state} {...rest}>
                        {children}
                    </RouterLink>
                )
            },
            [resolveFromTo]
        )

        return useMemo(() => ({ navigate, Link, preview }), [navigate, Link, preview])
    }

    return { routes, useRouter }
}
